import { decoded, openOutput, play, wakeOnGestures } from "./fuxi-audio.js";
import {
  feedbackSoundAddress,
  feedbackSoundsBase,
} from "../user/fuxi-sound-addresses.js";

/**
 * What a hanzi building Exercise sounds like: 复习's right or wrong note once
 * a character is built, and then the character said aloud.
 *
 * The notes are the ones `./lianxi-verdict-sounds.ts` plays for the other
 * Exercises. **The character is said whether it was built right or wrong**,
 * since a learner who missed it has the most to gain from hearing it, and in
 * one of its recordings chosen at random each time, as the writing drill says
 * its character (`./xiezi-sounds.ts` has why). The recordings are the
 * character's own entry's, which `partials/lianxi/pinzi.html` writes onto each
 * item, and they are fetched and decoded as the Exercise starts so the word
 * follows the note with no fetch in between.
 *
 * A browser with no Web Audio, or a file that cannot be fetched, leaves the
 * Exercise silent and marking as before.
 */

/** What a hanzi building Exercise asks of its sounds. */
export interface PinziSounds {
  /** Fetch and decode recordings before a character that needs them is built. */
  preload(recordings: readonly string[]): void;

  /** Play the note for a verdict, then say the character in one of its recordings. */
  mark(right: boolean, recordings: readonly string[]): void;
}

/** How loudly a note plays, as `./lianxi-verdict-sounds.ts` plays it. */
const noteVolume = 0.25;

/** How loudly the character is said, as a 复习 card says its word. */
const wordVolume = 1;

/**
 * How much of the end of the note the character is said over, in seconds,
 * the figure `./xiezi-sounds.ts` takes from 复习.
 */
const noteTail = 0.15;

/** The addresses in a random order, so each voice is as likely to be said. */
function shuffled(addresses: readonly string[]): readonly string[] {
  return addresses
    .map((address) => ({ address, at: Math.random() }))
    .toSorted((one, other) => one.at - other.at)
    .map(({ address }) => address);
}

/**
 * Whichever of the recordings loads first, or nothing once none has.
 *
 * Every recording is waited on at once, so one still loading never holds
 * up another that is ready. Among recordings already loaded, the one first
 * in `addresses` wins, which is why they are shuffled before they get here.
 */
export async function firstLoaded(
  addresses: readonly string[],
  load: (address: string) => Promise<AudioBuffer | undefined>,
): Promise<AudioBuffer | undefined> {
  try {
    return await Promise.any(
      addresses.map(async (address) => {
        const buffer = await load(address);

        if (buffer === undefined) {
          throw new Error(`${address} did not load.`);
        }

        return buffer;
      }),
    );
  } catch {
    return undefined;
  }
}

/**
 * Load the two notes, and say how to play them and the character.
 * @param page - The page the Exercise is on, whose gestures wake the output.
 * @param base - Where the notes are served from.
 * @returns What plays a built character's sounds, doing nothing where none can play.
 */
export function pinziSounds(
  page: Document,
  base: string = feedbackSoundsBase,
): PinziSounds {
  const output = openOutput();

  if (output === undefined) {
    return { preload: () => undefined, mark: () => undefined };
  }

  wakeOnGestures(page, output);

  const notes = new Map<boolean, AudioBuffer>();
  const recordings = new Map<string, Promise<AudioBuffer | undefined>>();
  let saying: AudioBufferSourceNode | undefined;
  let latest = 0;

  for (const right of [true, false]) {
    void decoded(
      output,
      feedbackSoundAddress(base, right ? "right" : "wrong"),
    ).then((buffer) => {
      if (buffer !== undefined) {
        notes.set(right, buffer);
      }
    });
  }

  const load = (address: string): Promise<AudioBuffer | undefined> => {
    const loading = recordings.get(address) ?? decoded(output, address);

    recordings.set(address, loading);

    return loading;
  };

  return {
    preload: (addresses): void => {
      for (const address of addresses) {
        void load(address);
      }
    },
    mark: (right, addresses): void => {
      if (output.state !== "running") {
        void output.resume().catch(() => undefined);
      }

      const note = notes.get(right);
      const at = output.currentTime;

      if (note !== undefined) {
        play({ output, buffer: note, at, gain: noteVolume });
      }

      const after = at + Math.max(0, (note?.duration ?? 0) - noteTail);
      const mark = ++latest;

      void firstLoaded(shuffled(addresses), load).then((word) => {
        // A later character marked while this one loaded is said instead.
        if (word === undefined || mark !== latest) {
          return;
        }

        saying?.stop();
        saying = play({
          output,
          buffer: word,
          at: Math.max(output.currentTime, after),
          gain: wordVolume,
        });
      });
    },
  };
}
